import io from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_BACKEND_SOCKET_URL || 'http://localhost:5000';

class SocketService {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    this.reconnectTimer = null;
    this.currentChannel = null;
    this.currentThreads = [];
    this.isManualDisconnect = false;
    this.pendingCallbacks = {}; // Store callbacks for re-registration after reconnection
    this.hubspotSyncContext = null; // Store HubSpot sync context for reconnection
  }

  connect() {
    if (this.socket && this.socket.connected) {
      console.log('Socket already connected');
      return this.socket;
    }

    this.isManualDisconnect = false;

    this.socket = io(SOCKET_URL, {
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      transports: ['websocket', 'polling']
    });

    this.setupEventHandlers();
    return this.socket;
  }

  setupEventHandlers() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket.id);
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;

      // Rejoin channel and threads after reconnection
      if (this.currentChannel) {
        console.log('Rejoining channel after reconnect:', this.currentChannel);
        if (this.hubspotSyncContext) {
          this.socket.emit('join-channel-with-sync', {
            channelId: this.currentChannel,
            hubspotSync: this.hubspotSyncContext
          });
        } else {
          this.socket.emit('join-channel', this.currentChannel);
        }
      }

      this.currentThreads.forEach(thread => {
        console.log('Rejoining thread after reconnect:', thread);
        this.socket.emit('join-thread', thread);
      });

      // Re-register pending callbacks after reconnection
      if (this.pendingCallbacks) {
        Object.entries(this.pendingCallbacks).forEach(([event, callback]) => {
          if (callback) {
            console.log('Re-registering callback for:', event);
            this.socket.off(event);
            this.socket.on(event, callback);
          }
        });
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);

      // Don't attempt to reconnect if it was a manual disconnect
      if (this.isManualDisconnect) {
        console.log('Manual disconnect, not attempting to reconnect');
        return;
      }

      // Socket.io will automatically try to reconnect for most disconnect reasons
      // except for 'io server disconnect' which requires manual reconnection
      if (reason === 'io server disconnect') {
        console.log('Server disconnected, attempting manual reconnect...');
        this.attemptReconnect();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached');
        return;
      }
    });

    this.socket.on('error', (error) => {
      console.error('Socket error:', error);
    });

    // Ping/pong to keep connection alive
    this.socket.on('ping', () => {
      this.socket.emit('pong');
    });
  }

  attemptReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    );

    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.socket.connect();
    }, delay);
  }

  disconnect() {
    this.isManualDisconnect = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.currentChannel = null;
    this.currentThreads = [];
    this.reconnectAttempts = 0;
  }

  isConnected() {
    return this.socket && this.socket.connected;
  }

  joinChannel(channelId) {
    this.currentChannel = channelId;
    if (this.socket) {
      this.socket.emit('join-channel', channelId);
    }
  }

  // Join channel with HubSpot sync context
  joinChannelWithSync(channelId, hubspotSync) {
    this.currentChannel = channelId;
    this.hubspotSyncContext = hubspotSync;
    if (this.socket) {
      this.socket.emit('join-channel-with-sync', { channelId, hubspotSync });
    }
  }

  // Update sync settings for current channel
  updateSyncSettings(hubspotSync) {
    this.hubspotSyncContext = hubspotSync;
    if (this.socket && this.currentChannel) {
      this.socket.emit('update-sync-settings', {
        channelId: this.currentChannel,
        hubspotSync
      });
    }
  }

  leaveChannel(channelId) {
    if (this.currentChannel === channelId) {
      this.currentChannel = null;
      this.hubspotSyncContext = null;
    }
    if (this.socket) {
      this.socket.emit('leave-channel', channelId);
    }
  }

  onNewMessage(callback) {
    this.pendingCallbacks['new-message'] = callback;
    if (this.socket) {
      this.socket.off('new-message');
      this.socket.on('new-message', callback);
      console.log('Registered new-message listener');
    }
  }

  offNewMessage() {
    delete this.pendingCallbacks['new-message'];
    if (this.socket) {
      this.socket.off('new-message');
    }
  }

  onMessageUpdated(callback) {
    this.pendingCallbacks['message-updated'] = callback;
    if (this.socket) {
      this.socket.off('message-updated');
      this.socket.on('message-updated', callback);
      console.log('Registered message-updated listener');
    }
  }

  offMessageUpdated() {
    delete this.pendingCallbacks['message-updated'];
    if (this.socket) {
      this.socket.off('message-updated');
    }
  }

  onMessageDeleted(callback) {
    this.pendingCallbacks['message-deleted'] = callback;
    if (this.socket) {
      this.socket.off('message-deleted');
      this.socket.on('message-deleted', callback);
      console.log('Registered message-deleted listener');
    }
  }

  offMessageDeleted() {
    delete this.pendingCallbacks['message-deleted'];
    if (this.socket) {
      this.socket.off('message-deleted');
    }
  }

  joinThread(channelId, threadTs) {
    const threadKey = { channelId, threadTs };
    // Add to tracked threads if not already present
    if (!this.currentThreads.some(t => t.channelId === channelId && t.threadTs === threadTs)) {
      this.currentThreads.push(threadKey);
    }
    if (this.socket) {
      this.socket.emit('join-thread', threadKey);
    }
  }

  leaveThread(channelId, threadTs) {
    // Remove from tracked threads
    this.currentThreads = this.currentThreads.filter(
      t => !(t.channelId === channelId && t.threadTs === threadTs)
    );
    if (this.socket) {
      this.socket.emit('leave-thread', { channelId, threadTs });
    }
  }

  onThreadReply(callback) {
    this.pendingCallbacks['thread-reply'] = callback;
    if (this.socket) {
      this.socket.off('thread-reply');
      this.socket.on('thread-reply', callback);
      console.log('Registered thread-reply listener');
    }
  }

  offThreadReply() {
    delete this.pendingCallbacks['thread-reply'];
    if (this.socket) {
      this.socket.off('thread-reply');
    }
  }

  onReactionAdded(callback) {
    this.pendingCallbacks['reaction-added'] = callback;
    if (this.socket) {
      this.socket.off('reaction-added');
      this.socket.on('reaction-added', callback);
      console.log('Registered reaction-added listener');
    }
  }

  offReactionAdded() {
    delete this.pendingCallbacks['reaction-added'];
    if (this.socket) {
      this.socket.off('reaction-added');
    }
  }

  onReactionRemoved(callback) {
    this.pendingCallbacks['reaction-removed'] = callback;
    if (this.socket) {
      this.socket.off('reaction-removed');
      this.socket.on('reaction-removed', callback);
      console.log('Registered reaction-removed listener');
    }
  }

  offReactionRemoved() {
    delete this.pendingCallbacks['reaction-removed'];
    if (this.socket) {
      this.socket.off('reaction-removed');
    }
  }

  getSocket() {
    return this.socket;
  }
}

export default new SocketService();